import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import Route from 'cio/routes/requires-trigger-type-route';
import { task, timeout } from 'ember-concurrency';
import config from 'cio/config/environment';
import { CONFIRM_TRANSITION } from 'cio/services/event-bus';
import { createActionAbstract } from 'cio/utils/visual-workflow-builder/action-abstracts';
import { isNone } from '@ember/utils';
import { all } from 'rsvp';
import { VWBTab } from 'cio/utils/visual-workflow-builder/types';
import isMultiLangBranchChild from 'cio/utils/visual-workflow-builder/is-multi-lang-branch-child';

const ACTION_SUBJECTS_POLL_INTERVAL = config.APP.actionSubjectsReloadInterval;

export default class BaseSetupWorkflowActionsRoute extends Route {
  templateName = 'base/setup/workflow/actions';

  @service actionSubjects;
  @service eventBus;
  @service filters;
  @service visualWorkflowBuilder;
  @service messageSettings;
  @service store;
  @service confirmChanges;

  async afterModel(model) {
    await super.afterModel(...arguments);

    return all([
      this.store.findAll('short-segment'),
      this.store.findAll('collection'),
      this.filters.process.perform(),
      this.messageSettings.loadMessagingLimit.perform(),
      model?.parseTree(),
      model?.isWebhookTriggered ? model.fetchLatestWebhookLog(true) : null,
    ]);
  }

  setupController(controller, model) {
    super.setupController(...arguments);

    // Can't just use `campaign.get('actions')` here, as deleting the model
    // in another tab will not remove it from our identity cache, and the
    // has-many association is based on the foreign key in the action model,
    // not anything returned in our model hook.
    let selectedAction = controller.get('selectedActionAbstract.action');
    let splitActions = model.get('splitTestActions');
    let subActions = splitActions.map((split) => split.get('actions').toArray());
    let campaignActions = model.get('workflowActions').concat(...subActions);

    if (!campaignActions.includes(selectedAction)) {
      controller.set('selectedActionAbstract', null);
    }

    if (controller.actionId && !controller.selectedActionAbstract) {
      let action = campaignActions.findBy('id', controller.actionId);

      if (isMultiLangBranchChild(action)) {
        action = model.getConsolidatedActionsGroupDefaultActionByChildActionId(action.id);
      }

      controller.setProperties({
        selectedActionAbstract: !isNone(action) ? createActionAbstract(action) : null,
      });
    }

    if (!model.isDraft) {
      // If this is a live campaign, start polling to ensure delays show the
      // latest people counts
      this.get('pollActionSubjects').perform(model.get('id'));
    }

    controller.setInitialMetricsTabMeasures();
    this.confirmChanges.register(controller, this);
  }

  resetController(controller) {
    super.resetController(...arguments);

    controller.setProperties({
      actionId: null,
      selectedActionAbstract: null,
      tab: VWBTab.Build,
      highlightedNodeIds: [],
    });

    // The "copy from" and "copy to" modals create temporary models in the Ember
    // Data store, since they render the VWB, and a lot of its internals rely on
    // Campaign and Action model functionality. We need to make sure to clear
    // those entries when the modals close.
    this.store
      .peekAll('campaign')
      .filter((c) => c._copy)
      .forEach((c) => c.unloadRecord());
    this.store
      .peekAll('action')
      .filter((a) => a._copy)
      .forEach((a) => a.unloadRecord());
  }

  pollActionSubjects = task(
    {
      restartable: true,
      cancelOn: 'deactivate',
    },
    async (campaignId) => {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        try {
          await this.get('actionSubjects.fetch').perform(campaignId);
        } catch (e) {
          // Silently ignore failure
        }
        if (ACTION_SUBJECTS_POLL_INTERVAL) {
          await timeout(ACTION_SUBJECTS_POLL_INTERVAL);
        } else {
          break;
        }
      }
    },
  );

  @action
  didTransition() {
    // Resetting query params on the controller will fire another transition.
    // That doesn't really interfere with anything, but it will cause another
    // analytics tracking event, which is annoying.
    // There's now an extra check in utils/analytics/generate-transition-event
    // to prevent this additional tracking event from happening.
    this.controller.set('actionId', null);
    return true;
  }

  @action
  willTransition(transition) {
    if (this.controller.selectedActionAbstract?.isDirty) {
      transition.abort();
      this.eventBus.publish(CONFIRM_TRANSITION, transition);
    } else {
      // Clear out the selected action unless we're going to the composer
      if (!/composer/.test(transition.targetName)) {
        this.controller.set('selectedActionAbstract', null);
      }

      return true;
    }
  }
}
